import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, Mail, Lock, Eye, EyeOff, User, Calendar, Phone, CheckCircle2, AlertCircle, FileText, Shield, X } from 'lucide-react';
import WelcomePopup from '../components/WelcomePopup';

// Use dynamic API URL that works on both dev and production
const getApiUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname.includes('preview.emergentagent.com')) {
    return process.env.REACT_APP_BACKEND_URL || '';
  }
  return window.location.origin;
};
const API = `${getApiUrl()}/api`;

const Signup = ({ onLogin }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState('form'); // form, otp, success
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    dob: '',
    phone: '',
    referralCode: ''
  });
  const [otp, setOtp] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [demoOtp, setDemoOtp] = useState(null);
  const [showReferral, setShowReferral] = useState(false);
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  const [welcomeData, setWelcomeData] = useState({ userNumber: 1, userName: '' });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [showPrivacyDialog, setShowPrivacyDialog] = useState(false);

  const calculateAge = (dob) => {
    if (!dob) return null;
    const birth = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  const validateForm = () => {
    if (!formData.name.trim() || formData.name.length < 2) {
      toast.error('Name must be at least 2 characters');
      return false;
    }
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('Please enter a valid email');
      return false;
    }
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return false;
    }
    const age = calculateAge(formData.dob);
    if (!age || age < 18) {
      toast.error('You must be at least 18 years old');
      return false;
    }
    if (formData.phone && !/^[6-9]\d{9}$/.test(formData.phone.replace(/\D/g, ''))) {
      toast.error('Please enter a valid 10-digit Indian mobile number');
      return false;
    }
    if (!termsAccepted || !privacyAccepted) {
      toast.error('Please accept the Terms of Service and Privacy Policy');
      return false;
    }
    return true;
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/signup`, {
        name: formData.name.trim(),
        email: formData.email.toLowerCase().trim(),
        password: formData.password,
        dob: formData.dob,
        phone: formData.phone || null,
        terms_accepted: termsAccepted,
        privacy_accepted: privacyAccepted,
        terms_accepted_at: new Date().toISOString()
      });
      
      setStep('otp');
      
      // If demo mode, show OTP
      if (response.data.demo_otp) {
        setDemoOtp(response.data.demo_otp);
      }
      
      toast.success('OTP sent to your email!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      toast.error('Please enter a 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/auth/verify-otp?email=${encodeURIComponent(formData.email)}&otp=${otp}`
      );
      
      const user = response.data.user;
      const userNumber = response.data.user_number || 1;
      const isNewUser = response.data.is_new_user;
      
      // Apply referral code if provided
      if (formData.referralCode.trim()) {
        try {
          await axios.post(`${API}/referral/apply?user_id=${user.id}&referral_code=${formData.referralCode.trim()}`);
          toast.success('Referral code applied! +100 XP bonus! 🎁');
        } catch (refError) {
          console.log('Referral code error:', refError.response?.data?.detail);
          // Don't block signup if referral fails
        }
      }
      
      // Show welcome popup for new users
      if (isNewUser) {
        setWelcomeData({ userNumber, userName: user.name || formData.name });
        setShowWelcomePopup(true);
        // Store user data for after popup closes
        window._pendingLoginUser = user;
      } else {
        setStep('success');
        toast.success('Email verified! 🎉');
        setTimeout(() => {
          onLogin(user);
          navigate('/dashboard');
        }, 2000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleWelcomePopupClose = () => {
    setShowWelcomePopup(false);
    const user = window._pendingLoginUser;
    if (user) {
      delete window._pendingLoginUser;
      onLogin(user);
      navigate('/dashboard');
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/resend-otp?email=${encodeURIComponent(formData.email)}`);
      if (response.data.demo_otp) {
        setDemoOtp(response.data.demo_otp);
      }
      toast.success('New OTP sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      setFormData({ ...formData, [name]: value.replace(/\D/g, '').slice(0, 10) });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const age = calculateAge(formData.dob);

  // Success Screen
  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50 dark:from-slate-950 dark:via-slate-900 dark:to-violet-950 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-card rounded-3xl p-8 shadow-xl border border-border max-w-md w-full text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', delay: 0.2 }}
            className="w-20 h-20 mx-auto mb-6 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center"
          >
            <CheckCircle2 className="w-10 h-10 text-emerald-600" />
          </motion.div>
          <h2 className="text-2xl font-bold mb-2">Welcome to SharePay! 🎉</h2>
          <p className="text-muted-foreground mb-4">Your account is ready. Redirecting to dashboard...</p>
          <p className="text-sm text-violet-600">+50 XP earned for signing up!</p>
        </motion.div>
      </div>
    );
  }

  // OTP Verification Screen
  if (step === 'otp') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50 dark:from-slate-950 dark:via-slate-900 dark:to-violet-950">
        {/* Welcome Popup */}
        <WelcomePopup
          isOpen={showWelcomePopup}
          onClose={handleWelcomePopupClose}
          userNumber={welcomeData.userNumber}
          userName={welcomeData.userName}
        />
        
        <div className="max-w-md mx-auto px-4 py-8">
          <Button 
            variant="ghost" 
            onClick={() => setStep('form')}
            className="mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card rounded-3xl p-8 shadow-xl border border-border"
          >
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                <Mail className="w-8 h-8 text-violet-600" />
              </div>
              <h1 className="text-2xl font-bold mb-2">Verify Your Email</h1>
              <p className="text-muted-foreground">
                We sent a 6-digit code to<br/>
                <strong>{formData.email}</strong>
              </p>
            </div>

            {/* Demo OTP Display */}
            {demoOtp && (
              <div className="bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-800 rounded-xl p-4 mb-6 text-center">
                <p className="text-xs text-violet-600 dark:text-violet-400 font-medium mb-1">🔧 Demo Mode - Your OTP</p>
                <p className="text-3xl font-mono font-bold tracking-[0.5em] text-violet-700 dark:text-violet-300" data-testid="demo-otp">
                  {demoOtp}
                </p>
              </div>
            )}

            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <div>
                <Label htmlFor="otp">Enter OTP</Label>
                <Input
                  id="otp"
                  type="tel"
                  placeholder="000000"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="mt-2 text-center text-3xl font-mono tracking-[0.5em] rounded-xl"
                  maxLength={6}
                  data-testid="otp-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full rounded-full py-6 font-semibold gradient-primary"
                disabled={loading || otp.length !== 6}
                data-testid="verify-otp-btn"
              >
                {loading ? 'Verifying...' : 'Verify Email'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-muted-foreground text-sm">
                Didn't receive the code?{' '}
                <button 
                  onClick={handleResendOtp}
                  disabled={loading}
                  className="text-violet-600 font-semibold hover:underline"
                >
                  Resend OTP
                </button>
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Signup Form
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50 dark:from-slate-950 dark:via-slate-900 dark:to-violet-950">
      {/* SEO */}
      <Helmet>
        <title>Sign Up Free | SharePay India - Start Saving Together</title>
        <meta name="description" content="Join SharePay India for free! Split bills via UPI, create group savings goals, earn XP rewards. Made in India. Start your financial journey today!" />
        <link rel="canonical" href="https://sharepay.today/signup" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <div className="max-w-md mx-auto px-4 py-8">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/')}
          className="mb-6"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
        </Button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card rounded-3xl p-8 shadow-xl border border-border"
        >
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold gradient-text mb-2">Create Account</h1>
            <p className="text-muted-foreground">Join SharePay and start saving together!</p>
          </div>

          {/* Google Sign Up Button */}
          <Button
            type="button"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + '/auth/callback';
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            variant="outline"
            className="w-full rounded-full py-6 font-semibold mb-6 flex items-center justify-center gap-3"
            data-testid="google-signup-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">Or sign up with email</span>
            </div>
          </div>

          <form onSubmit={handleSignup} className="space-y-4">
            {/* Name */}
            <div>
              <Label htmlFor="name">Full Name *</Label>
              <div className="relative mt-2">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={handleChange}
                  className="pl-10 rounded-xl"
                  data-testid="name-input"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="email">Email *</Label>
              <div className="relative mt-2">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  className="pl-10 rounded-xl"
                  data-testid="email-input"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <Label htmlFor="password">Password * (min 8 characters)</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  className="pl-10 pr-10 rounded-xl"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <Label htmlFor="confirmPassword">Confirm Password *</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="pl-10 rounded-xl"
                  data-testid="confirm-password-input"
                />
              </div>
            </div>

            {/* Date of Birth */}
            <div>
              <Label htmlFor="dob">Date of Birth * (Must be 18+)</Label>
              <div className="relative mt-2">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="dob"
                  name="dob"
                  type="date"
                  value={formData.dob}
                  onChange={handleChange}
                  max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                  className="pl-10 rounded-xl"
                  data-testid="dob-input"
                />
              </div>
              {formData.dob && (
                <p className={`text-xs mt-1 flex items-center gap-1 ${age >= 18 ? 'text-emerald-600' : 'text-red-500'}`}>
                  {age >= 18 ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                  Age: {age} years {age >= 18 ? '✓' : '(Must be 18+)'}
                </p>
              )}
            </div>

            {/* Phone (Optional) */}
            <div>
              <Label htmlFor="phone">Mobile Number (Optional - Indian only)</Label>
              <div className="relative mt-2">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center">
                  <Phone className="w-4 h-4 text-muted-foreground mr-1" />
                  <span className="text-sm text-muted-foreground">+91</span>
                </div>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="9876543210"
                  value={formData.phone}
                  onChange={handleChange}
                  className="pl-20 rounded-xl"
                  data-testid="phone-input"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">For UPI payments (not required for signup)</p>
            </div>

            {/* Referral Code (Optional) */}
            <div className="pt-2">
              {!showReferral ? (
                <button
                  type="button"
                  onClick={() => setShowReferral(true)}
                  className="text-sm text-violet-600 hover:text-violet-500 font-medium flex items-center gap-1"
                >
                  <span className="text-lg">🎁</span> Have a referral code?
                </button>
              ) : (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  transition={{ duration: 0.3 }}
                >
                  <Label htmlFor="referralCode">Referral Code (Optional)</Label>
                  <div className="relative mt-2">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-lg">🎁</span>
                    <Input
                      id="referralCode"
                      name="referralCode"
                      type="text"
                      placeholder="Enter friend's code"
                      value={formData.referralCode}
                      onChange={handleChange}
                      className="pl-10 rounded-xl uppercase"
                      data-testid="referral-input"
                    />
                  </div>
                  <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" />
                    You & your friend both get +100 XP bonus!
                  </p>
                </motion.div>
              )}
            </div>

            {/* Terms and Privacy Agreement */}
            <div className="pt-4 space-y-3">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="terms"
                  checked={termsAccepted}
                  onCheckedChange={setTermsAccepted}
                  className="mt-0.5"
                  data-testid="terms-checkbox"
                />
                <label htmlFor="terms" className="text-sm text-muted-foreground leading-tight">
                  I agree to the{' '}
                  <button
                    type="button"
                    onClick={() => setShowTermsDialog(true)}
                    className="text-violet-600 hover:underline font-medium"
                  >
                    Terms of Service
                  </button>
                </label>
              </div>
              
              <div className="flex items-start gap-3">
                <Checkbox
                  id="privacy"
                  checked={privacyAccepted}
                  onCheckedChange={setPrivacyAccepted}
                  className="mt-0.5"
                  data-testid="privacy-checkbox"
                />
                <label htmlFor="privacy" className="text-sm text-muted-foreground leading-tight">
                  I agree to the{' '}
                  <button
                    type="button"
                    onClick={() => setShowPrivacyDialog(true)}
                    className="text-violet-600 hover:underline font-medium"
                  >
                    Privacy Policy
                  </button>
                </label>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full rounded-full py-6 font-semibold gradient-primary mt-6"
              disabled={loading || !termsAccepted || !privacyAccepted}
              data-testid="signup-btn"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>

          {/* Terms Dialog */}
          <Dialog open={showTermsDialog} onOpenChange={setShowTermsDialog}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-violet-600" />
                  Terms of Service
                </DialogTitle>
              </DialogHeader>
              <div className="prose dark:prose-invert text-sm space-y-4">
                <p className="text-muted-foreground">Last updated: February 2026</p>
                
                <h4 className="font-semibold">1. Acceptance of Terms</h4>
                <p>By accessing and using SharePay, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our platform.</p>
                
                <h4 className="font-semibold">2. Description of Service</h4>
                <p>SharePay is a platform that enables groups to save money together, split expenses, and achieve financial goals collaboratively. We do not handle actual money transfers - all payments are made directly between users via UPI or other payment methods.</p>
                
                <h4 className="font-semibold">3. User Responsibilities</h4>
                <ul className="list-disc pl-5 space-y-1">
                  <li>You must be at least 18 years old to use SharePay</li>
                  <li>You are responsible for maintaining the confidentiality of your account</li>
                  <li>You agree to provide accurate and complete information</li>
                  <li>You are solely responsible for all transactions and payments made through the platform</li>
                </ul>
                
                <h4 className="font-semibold">4. Payment Disclaimer</h4>
                <p className="bg-amber-50 dark:bg-amber-950/30 p-3 rounded-lg border border-amber-200 dark:border-amber-800">
                  <strong>Important:</strong> SharePay facilitates communication between users but does not process, hold, or guarantee any financial transactions. All payments are made directly between users. SharePay is not responsible for any payment disputes, failed transactions, or financial losses.
                </p>
                
                <h4 className="font-semibold">5. Limitation of Liability</h4>
                <p>SharePay shall not be liable for any direct, indirect, incidental, special, or consequential damages resulting from the use or inability to use our service.</p>
                
                <h4 className="font-semibold">6. Changes to Terms</h4>
                <p>We reserve the right to modify these terms at any time. Continued use of SharePay after changes constitutes acceptance of the new terms.</p>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowTermsDialog(false)}>Close</Button>
                <Button 
                  onClick={() => { setTermsAccepted(true); setShowTermsDialog(false); }}
                  className="gradient-primary"
                >
                  Accept Terms
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Privacy Dialog */}
          <Dialog open={showPrivacyDialog} onOpenChange={setShowPrivacyDialog}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-violet-600" />
                  Privacy Policy
                </DialogTitle>
              </DialogHeader>
              <div className="prose dark:prose-invert text-sm space-y-4">
                <p className="text-muted-foreground">Last updated: February 2026</p>
                
                <h4 className="font-semibold">1. Information We Collect</h4>
                <p>We collect information you provide directly to us, including:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Name, email address, and date of birth</li>
                  <li>Phone number (optional, for UPI payments)</li>
                  <li>Group membership and contribution data</li>
                  <li>Usage data and analytics</li>
                </ul>
                
                <h4 className="font-semibold">2. How We Use Your Information</h4>
                <p>We use your information to:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Provide and improve our services</li>
                  <li>Send notifications about your groups and contributions</li>
                  <li>Prevent fraud and ensure platform security</li>
                  <li>Communicate with you about updates and features</li>
                </ul>
                
                <h4 className="font-semibold">3. Data Security</h4>
                <p>We implement industry-standard security measures to protect your data. However, no method of transmission over the Internet is 100% secure.</p>
                
                <h4 className="font-semibold">4. Data Sharing</h4>
                <p>We do not sell your personal information. We may share data with:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Other users in your groups (as necessary for the service)</li>
                  <li>Service providers who help us operate SharePay</li>
                  <li>Law enforcement when required by law</li>
                </ul>
                
                <h4 className="font-semibold">5. Your Rights</h4>
                <p>You have the right to access, update, or delete your personal information at any time through your account settings.</p>
                
                <h4 className="font-semibold">6. Contact Us</h4>
                <p>For privacy-related questions, contact us at support@sharepay.today</p>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowPrivacyDialog(false)}>Close</Button>
                <Button 
                  onClick={() => { setPrivacyAccepted(true); setShowPrivacyDialog(false); }}
                  className="gradient-primary"
                >
                  Accept Privacy Policy
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <div className="mt-6 text-center">
            <p className="text-muted-foreground">
              Already have an account?{' '}
              <Link to="/login" className="text-violet-600 font-semibold hover:underline">
                Sign In
              </Link>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Signup;
